import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  // Track whether initial session check is done to avoid race with onAuthStateChange
  const initializedRef = useRef(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        // PGRST116 = no rows found — normal for new users before profile creation
        if (!error) {
          setUserProfile(data)
        } else if (error?.code !== 'PGRST116') {
          console.error('Profile load error:', error)
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true

    // Subscribe to auth state changes FIRST so we don't miss events
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        if (!mounted) return
        console.debug('[AuthContext] onAuthStateChange event:', event, 'user:', session?.user?.id ?? null)

        // If initial getSession hasn't resolved yet, let it handle the first state
        // For subsequent events (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED), always update
        setUser(session?.user ?? null)
        setLoading(false)
        initializedRef.current = true

        if (session?.user) {
          profileOperations?.load(session?.user?.id) // Fire-and-forget
        } else {
          profileOperations?.clear()
        }
      }
    )

    // Then do the initial session check
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (!mounted) return
      if (error) {
        console.error('[AuthContext] getSession error:', error)
      }
      console.debug('[AuthContext] getSession result — user:', session?.user?.id ?? null)

      // Only set state from getSession if onAuthStateChange hasn't fired yet
      // This prevents a flicker where loading briefly shows wrong state
      if (!initializedRef?.current) {
        setUser(session?.user ?? null)
        setLoading(false)
        initializedRef.current = true

        if (session?.user) {
          profileOperations?.load(session?.user?.id)
        }
      }
    })?.catch((err) => {
      if (!mounted) return
      console.error('[AuthContext] getSession threw:', err)
      if (!initializedRef?.current) {
        setLoading(false)
        initializedRef.current = true
      }
    })

    return () => {
      mounted = false
      subscription?.unsubscribe()
    }
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    if (!isSupabaseConfigured) {
      return { error: { message: 'Authentication service is not configured. Please contact support.' } };
    }
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      const isNetworkError = error instanceof TypeError ||
        error?.message?.includes('fetch') ||
        error?.message?.includes('Network')
      const message = isNetworkError
        ? 'Unable to connect. Please check your internet connection and try again.' : 'Sign in failed. Please try again.'
      return { error: { message } }
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    if (!isSupabaseConfigured) {
      return { error: { message: 'Authentication service is not configured. Please contact support.' } };
    }

    // Ensure role is a valid string, fallback to influencer
    // Admin accounts can only be created via Supabase Dashboard — never via self-registration
    const allowedRoles = ['influencer', 'brand']
    const safeRole = allowedRoles?.includes(metadata?.role) ? metadata?.role : 'influencer'

    const safeMetadata = {
      full_name: metadata?.full_name || '',
      role: safeRole,
    }

    console.debug('[AuthContext] signUp called for:', email, 'role:', safeMetadata?.role)

    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: safeMetadata,
          emailRedirectTo: `${window.location?.origin}/auth/callback`
        }
      })

      console.debug('[AuthContext] signUp response — data:', data, 'error:', error)

      if (error) {
        console.error('[AuthContext] signUp error:', error)
        // Detect retryable fetch / network errors returned as error objects (not thrown)
        const errMsg = error?.message || ''
        const isNetworkErr = errMsg?.toLowerCase()?.includes('fetch') ||
          errMsg?.toLowerCase()?.includes('network') ||
          errMsg?.toLowerCase()?.includes('failed') ||
          error?.name === 'AuthRetryableFetchError'
        if (isNetworkErr) {
          return {
            error: {
              message: 'Unable to reach the authentication server. Your Supabase project may be paused — please visit your Supabase dashboard to resume it, then try again.'
            }
          }
        }
      }

      return { data, error }
    } catch (err) {
      console.error('[AuthContext] signUp threw exception:', err)
      const isNetworkError = err instanceof TypeError ||
        err?.message?.toLowerCase()?.includes('fetch') ||
        err?.message?.toLowerCase()?.includes('network') ||
        err?.message?.toLowerCase()?.includes('failed') ||
        err?.name === 'AuthRetryableFetchError'
      const message = isNetworkError
        ? 'Unable to reach the authentication server. Your Supabase project may be paused — please visit your Supabase dashboard to resume it, then try again.' :'Failed to create account. Please try again.'
      return { error: { message } }
    }
  }

  const signInWithGoogle = async (role = 'influencer') => {
    if (!isSupabaseConfigured) {
      return { error: { message: 'Authentication service is not configured. Please contact support.' } };
    }
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/auth/callback?role=${role}`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Google sign-in failed. Please try again.' } }
    }
  }

  const resetPassword = async (email) => {
    if (!isSupabaseConfigured) {
      return { error: { message: 'Authentication service is not configured. Please contact support.' } };
    }
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/login-screen`
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }

    try {
      // Use upsert so profile row is created if the trigger didn't fire
      const upsertData = {
        id: user?.id,
        email: user?.email,
        full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || '',
        role: user?.user_metadata?.role || 'influencer',
        ...updates,
      }
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.upsert(upsertData, { onConflict: 'id' })
        ?.select()
        ?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Derive role from userProfile or user metadata
  const userRole = userProfile?.role ||
    user?.user_metadata?.role ||
    user?.raw_user_meta_data?.role ||
    null

  const isProfileVerified = userProfile?.profile_verified === true || userRole === 'admin'

  const value = {
    user,
    userProfile,
    userRole,
    loading,
    profileLoading,
    isProfileVerified,
    signIn,
    signUp,
    signInWithGoogle,
    resetPassword,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
